import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  RefreshCw,
  Users,
} from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../store/store";
import AdminLayout from "../../../components/AdminLayout";
import {
  fetchWaitingLists,
} from "../../../store/reducers/slices/waitingListSlice";
import { useSearch } from "../../../utils/useSearch";
import { usePagination } from "../../../utils/usePagination";
import Pagination from "../../../components/Pagination";
import {
  AdminAvatar,
  AdminButtonGhost,
  AdminEmptyState,
  AdminLoader,
  AdminPageHeader,
  AdminStatCard,
  AdminToolbar,
  adminCardClass,
  formatDate,
} from "../../../components/admin/AdminUi";

export default function WaitingListTable() {
  const dispatch = useAppDispatch();

  const { waitingLists, loading, error } = useAppSelector(
    (state) => state.waitingList
  );

  useEffect(() => {
    dispatch(fetchWaitingLists());
  }, [dispatch]);

  const { search, setSearch, filteredData } = useSearch(waitingLists, [
    "firstname",
    "lastname",
    "email",
    "phone_number",
  ]);

  const { currentPage, totalPages, paginatedData, goToPage } = usePagination({
    data: filteredData,
    itemsPerPage: 10,
  });

  const joinedThisMonth = waitingLists.filter((entry) => {
    if (!entry.created_at) return false;

    const created = new Date(entry.created_at);
    const now = new Date();

    return (
      created.getMonth() === now.getMonth() &&
      created.getFullYear() === now.getFullYear()
    );
  }).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <AdminPageHeader
          eyebrow="Growth"
          title="Waiting list"
          description="People who registered early interest in the platform. Export or follow up as launch campaigns go out."
          actions={
            <AdminButtonGhost onClick={() => dispatch(fetchWaitingLists())}>
              <RefreshCw size={14} />
              Refresh
            </AdminButtonGhost>
          }
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <AdminStatCard
            label="Total signups"
            value={waitingLists.length}
            hint="All early interest"
            icon={Users}
            tone="blue"
          />

          <AdminStatCard
            label="Joined this month"
            value={joinedThisMonth}
            hint="New since the 1st"
            icon={Users}
            tone="green"
          />

          <AdminStatCard
            label="Matching search"
            value={filteredData.length}
            hint="Current filter results"
            icon={Users}
            tone="zinc"
          />
        </div>

        <AdminToolbar
          search={search}
          onSearch={setSearch}
          placeholder="Search name, email or phone number..."
        />

        {loading ? (
          <AdminLoader label="Loading waiting list..." />
        ) : error ? (
          <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-6 text-sm text-red-400">
            {error}
          </div>
        ) : waitingLists.length === 0 ? (
          <AdminEmptyState
            title="No waiting list entries"
            description="Early signups will appear here as landing page leads come in."
            icon={Users}
          />
        ) : filteredData.length === 0 ? (
          <AdminEmptyState
            title="No matching entries"
            description="Try a different name, email or phone number."
          />
        ) : (
          <>
            <div className="hidden lg:block">
              <div className={`${adminCardClass} overflow-hidden`}>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="border-b border-zinc-800 bg-zinc-900/70 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                      <tr>
                        <th className="px-4 py-3 font-bold">Name</th>
                        <th className="px-4 py-3 font-bold">Contact</th>
                        <th className="px-4 py-3 font-bold">Gender</th>
                        <th className="px-4 py-3 font-bold">Joined</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedData.map((item) => (
                        <motion.tr
                          key={item.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="border-b border-zinc-800/60 transition hover:bg-zinc-900/60 last:border-b-0"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <AdminAvatar
                                size="sm"
                                name={`${item.firstname} ${item.lastname}`}
                              />
                              <p className="text-sm font-semibold text-white">
                                {item.firstname} {item.lastname}
                              </p>
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <p className="flex items-center gap-2 text-xs text-zinc-300">
                              <Mail size={13} className="text-zinc-500" />
                              {item.email}
                            </p>
                            <p className="mt-1 flex items-center gap-2 text-xs text-zinc-500">
                              <Phone size={13} />
                              {item.phone_number || "—"}
                            </p>
                          </td>

                          <td className="px-4 py-3">
                            <span className="rounded-full border border-zinc-700 bg-zinc-800/60 px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest text-zinc-300">
                              {item.gender || "—"}
                            </span>
                          </td>

                          <td className="px-4 py-3 text-xs text-zinc-400">
                            {formatDate(item.created_at)}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
              {paginatedData.map((item) => (
                <article
                  key={item.id}
                  className={`${adminCardClass} space-y-3 p-4`}
                >
                  <div className="flex items-center gap-3">
                    <AdminAvatar
                      name={`${item.firstname} ${item.lastname}`}
                    />

                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-bold text-white">
                        {item.firstname} {item.lastname}
                      </h3>
                      <p className="truncate text-xs text-zinc-500">
                        {item.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t border-zinc-800/80 pt-3 text-[11px]">
                    <span className="flex items-center gap-1.5 text-zinc-400">
                      <Phone size={12} />
                      {item.phone_number || "—"}
                    </span>

                    <span className="capitalize text-zinc-500">
                      {item.gender || "—"} · {formatDate(item.created_at)}
                    </span>
                  </div>
                </article>
              ))}
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
}
